import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { XCircle, Upload, RefreshCw, FileText, ChevronDown } from "lucide-react";
import { StatusBadge } from "../components/shared/StatusBadges";
import { ChatButton } from "../components/chat/ChatButton";
import { ActiveOrderSupportReminder } from "../components/orders/ActiveOrderSupportReminder";
import { AdjustmentDecisionCard } from "../components/orders/AdjustmentDecisionCard";

/**
 * OrdersPage — user-facing "Historial y Estado de Pedidos" view.
 *
 * Extracted from App.js to keep that file below the 300-line-per-component
 * budget flagged in the code review. The component is intentionally "dumb":
 * it renders whatever `orders` is passed and delegates the "complete partial
 * payment" and "open chat" side-effects to callbacks. All fetching/writing
 * still happens in the parent App.
 */
export function OrdersPage({ orders, onCompletePartial, onOpenChat, onPayAdjustment, onAdjustmentResolved, showToast }) {
  return (
    <div className="space-y-6" data-testid="client-orders-view">
      <div className="relative overflow-hidden bg-[var(--ag7-glass)] backdrop-blur-xl border border-ag7-purple/25 rounded-2xl p-6">
        <div className="pointer-events-none absolute -top-14 right-0 w-44 h-44 rounded-full bg-ag7-cyan/15 blur-3xl" aria-hidden="true" />
        <div className="relative flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-ag7-cyan/15 border border-ag7-cyan/50 shadow-[0_0_18px_rgba(6,182,212,0.25)] flex items-center justify-center flex-shrink-0" aria-hidden="true">
            <FileText className="w-5 h-5 text-ag7-cyan" />
          </div>
          <div>
            <h3 className="text-xl font-heading font-black tracking-tight text-white uppercase">Historial y Estado de Pedidos</h3>
            <p className="text-xs text-[var(--ag7-text-muted)]">Tus recargas procesadas en AG7 Store.</p>
          </div>
        </div>
      </div>

      <ActiveOrderSupportReminder orders={orders} onOpenChat={onOpenChat} />

      {orders.length === 0 ? (
        <div className="text-center py-12 text-zinc-500 text-sm">Aún no tienes pedidos.</div>
      ) : (
        orders?.map((order) => (
          <OrderCard
            key={order.id}
            order={order}
            onCompletePartial={onCompletePartial}
            onOpenChat={onOpenChat}
            onPayAdjustment={onPayAdjustment}
            onAdjustmentResolved={onAdjustmentResolved}
            showToast={showToast}
          />
        ))
      )}
    </div>
  );
}

function OrderItemsList({ order }) {
  const items = order.items || [];
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2" data-testid={`client-order-items-${order.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        data-testid={`client-order-toggle-${order.id}`}
        className="w-full flex items-center justify-between gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white transition-colors">
        <span>{open ? "Ocultar detalles" : "Ver detalles"} · {items.length} recargas</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="items" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.28, ease: "easeInOut" }}
            className="overflow-hidden" data-testid={`client-order-items-panel-${order.id}`}>
      <div className="rounded-xl border border-[var(--ag7-border-soft)] bg-ag7-bg divide-y divide-[var(--ag7-border-soft)]">
        {items?.map((it, i) => (
          <div key={`${order.id}-${i}`} data-testid={`client-order-item-${order.id}-${i}`}
            className="flex items-center justify-between gap-3 px-3 py-2">
            <div className="min-w-0">
              <p className="text-xs font-heading font-bold text-white truncate">
                <span className="text-zinc-500 font-mono mr-1.5">#{i + 1}</span>{it.gameName} - {it.packageName}
              </p>
              <p className="text-[10px] font-mono text-zinc-500 truncate">ID: {it.playerId}</p>
            </div>
            <span className="text-xs text-ag7-cyan font-mono tabular-nums flex-shrink-0">Bs {Math.round(it.priceBs)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between gap-3 px-3 py-2 bg-ag7-surface/60 rounded-b-xl">
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Total del pedido</span>
          <span className="text-sm font-heading font-black text-ag7-cyan font-mono tabular-nums"
            data-testid={`client-order-total-${order.id}`}>Bs {Math.round(order.priceBs)}</span>
        </div>
      </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function OrderCard({ order, onCompletePartial, onOpenChat, onPayAdjustment, onAdjustmentResolved, showToast }) {
  const adj = order.adjustment || {};
  const multi = (order.items || []).length > 1;
  return (
    <div className="rounded-2xl bg-ag7-surface border border-[var(--ag7-border-soft)] hover:border-ag7-purple/40 transition-colors p-6 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[var(--ag7-border)] pb-3">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-mono font-bold text-ag7-purple">
              {order.orderNumber != null ? `#${String(order.orderNumber).padStart(4, "0")}` : order.id}
            </span>
            <StatusBadge status={order.status} />
          </div>
          <p className="text-xs text-zinc-500 mt-1 font-mono">{new Date(order.createdAt).toLocaleString("es-VE")}</p>
        </div>
        <div className="text-right">
          {multi ? (
            <p className="text-sm font-heading font-bold text-white" data-testid={`client-order-multi-title-${order.id}`}>
              {order.items.length} recargas
            </p>
          ) : (
            <p className="text-sm font-heading font-bold text-white">{order.gameName} - {order.packageName}</p>
          )}
          <p className="text-xs text-ag7-cyan font-mono tabular-nums">Bs {Math.round(order.priceBs)}</p>
        </div>
      </div>

      {multi && <OrderItemsList order={order} />}

      <div className="p-3 rounded-xl bg-ag7-bg border border-[var(--ag7-border-soft)] text-xs text-zinc-300">
        {order.status === "Pendiente" && "Pedido realizado con éxito. Verificaremos tu pago pronto (5-15 min)."}
        {order.status === "En Proceso" && (adj.paymentApprovedAt
          ? <span data-testid={`adjustment-approved-processing-${order.id}`}>✅ Diferencia aprobada. Tu nuevo pedido ({order.packageName}) está en proceso: pronto realizaremos la recarga.</span>
          : "🔄 Recarga en proceso")}
        {order.status === "Completado" && "✅ Recarga completa. Por favor verifica, ya tienes el pedido en tu cuenta."}
        {order.status === "Pago Incompleto" && (
          <span className="text-orange-300">
            ⚠️ Pago parcial detectado. Falta un total de <strong>Bs {Math.round(order.shortageBs || 0)}</strong> para completar tu pedido.
          </span>
        )}
        {order.status === "Rechazado" && (
          <div className="space-y-1.5">
            <p className="text-red-300 font-bold flex items-center gap-1.5">
              <XCircle className="w-4 h-4" /> Pedido rechazado por el administrador.
            </p>
            {order.rejectionReason && (
              <div
                data-testid={`client-rejection-reason-${order.id}`}
                className="p-2 rounded-lg bg-red-950/40 border border-red-500/40 text-red-100"
              >
                <span className="text-[10px] font-mono uppercase tracking-widest text-red-300 block mb-0.5">Motivo</span>
                <span>{order.rejectionReason}</span>
              </div>
            )}
            <p className="text-[11px] text-zinc-400">Si crees que hay un error, contacta al soporte.</p>
          </div>
        )}
        {adj.status === "awaiting_client" && (
          <AdjustmentDecisionCard order={order} onResolved={onAdjustmentResolved}
            showToast={showToast} />
        )}
        {adj.status === "rejected" && (
          <p className="text-red-300 font-bold flex items-center gap-1.5"
            data-testid={`adjustment-rejected-${order.id}`}>
            <XCircle className="w-4 h-4" /> Rechazaste el reajuste: el equipo de AG7 te contactará.
          </p>
        )}
        {(order.status === "Ajuste Pendiente" || order.status === "Ajuste Enviado")
          && adj.status !== "awaiting_client" && order.adjustment && (
          <div className="space-y-1.5" data-testid={`client-adjustment-banner-${order.id}`}>
            <p className="text-amber-300 font-bold flex items-center gap-1.5">
              <RefreshCw className="w-4 h-4" />
              {order.status === "Ajuste Pendiente"
                ? "El admin solicitó un ajuste a tu pedido."
                : "Ajuste enviado — esperando verificación del admin."}
            </p>
            <div className="p-2 rounded-lg bg-amber-950/40 border border-amber-500/40 text-amber-100">
              <span className="text-[10px] font-mono uppercase tracking-widest text-amber-300 block mb-0.5">Motivo</span>
              <span>{order.adjustment.note}</span>
            </div>
            <div className="p-2 rounded-lg bg-ag7-bg border border-[var(--ag7-border-soft)] text-[11px] font-mono grid grid-cols-2 gap-1">
              <span className="text-zinc-500">Nuevo paquete:</span>
              <strong className="text-white text-right">{order.adjustment.newPackageName}</strong>
              <span className="text-zinc-500">Diferencia:</span>
              <strong className="text-amber-300 text-right">Bs {Math.round(order.adjustment.differenceBs || 0)}</strong>
            </div>
          </div>
        )}
      </div>

      {order.status === "Ajuste Pendiente" && adj.status === "accepted" && (
        <button
          onClick={() => onPayAdjustment && onPayAdjustment(order)}
          data-testid={`pay-adjustment-${order.id}`}
          className="w-full bg-gradient-to-r from-amber-600 to-cyan-600 hover:opacity-90 text-white font-bold py-2.5 rounded-xl text-xs shadow-md flex items-center justify-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Completar Pago de Diferencia</span>
        </button>
      )}

      {order.status === "Pago Incompleto" && (
        <button
          onClick={() => onCompletePartial(order)}
          data-testid={`complete-payment-${order.id}`}
          className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:opacity-90 text-white font-bold py-2.5 rounded-xl text-xs shadow-md flex items-center justify-center gap-2"
        >
          <Upload className="w-4 h-4" />
          <span>Completar Pago (Adjuntar Nueva Referencia)</span>
        </button>
      )}

      {order.additionalPayments && order.additionalPayments.length > 0 && (
        <div className="p-2 rounded-lg bg-cyan-950/30 border border-cyan-500/30 text-cyan-200 text-[11px] space-y-1">
          <p className="font-bold">Pagos adicionales:</p>
          {order.additionalPayments?.map((ap) => (
            <p key={ap.referenceNumber || ap.aiReference || ap.verifiedAt} className="font-mono">
              Ref {ap.referenceNumber || ap.aiReference} — Bs {Math.round(ap.paidAmountBs)}
            </p>
          ))}
        </div>
      )}

      {order.surplusBs > 0 && (
        <div className="p-2 rounded-lg bg-green-950/40 border border-green-500/30 text-green-300 text-[11px]">
          💰 Excedente {order.surplusCredited ? "acreditado" : "por acreditar (en validación)"}: Bs {Math.round(order.surplusBs)}
        </div>
      )}

      <ChatButton
        order={order}
        onOpen={() => onOpenChat(order)}
        highlight={!["Completado", "Rechazado"].includes(order.status)}
      />
    </div>
  );
}
